import React, { useState } from 'react';
import { X, Mail, Phone, Eye, EyeOff } from 'lucide-react';
import { supabase } from './supabaseClient';

const LoginModal = ({ 
  showLoginModal, 
  setShowLoginModal, 
  setShowLandingPage 
}) => {
  const [loginStep, setLoginStep] = useState('credentials');
  const [loginMethod, setLoginMethod] = useState('email');
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!showLoginModal) return null;

  const handleLogin = async () => {
    setError('');

    if (!email) {
      setError('Please enter your email');
      return;
    }

    if (!password) {
      setError('Please enter your password');
      return;
    }

    setLoading(true);

    try {
      // Send OTP for verification
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: email.toLowerCase(),
        options: {
          shouldCreateUser: false
        }
      });
      
      if (otpError) throw otpError;

      // Move to OTP step
      setLoginStep('otp');
      alert('Verification code sent! Check your email for 2FA code.');
      
    } catch (error) {
      setError(error.message || 'Login failed. Please try again.');
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      setError('Please enter the 6-digit verification code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Verify the OTP code
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email: email.toLowerCase(),
        token: otp,
        type: 'email'
      });

      if (verifyError) throw verifyError;

      // After OTP verified, sign out from OTP session
      await supabase.auth.signOut();

      // Now login with password
      const { error: passwordError } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase(),
        password: password
      });

      if (passwordError) throw passwordError;

      // Success!
      alert('Login successful!');
      setShowLoginModal(false);
      setShowLandingPage(false);
      resetModal();
      
    } catch (error) {
      setError('Invalid verification code or password. Please try again.');
      console.error('OTP verification error:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    setLoginStep('credentials');
    setEmail('');
    setPhone('');
    setPassword('');
    setOtp('');
    setError('');
  };

  const closeModal = () => {
    setShowLoginModal(false);
    resetModal();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-purple-800 rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold">
            {loginStep === 'credentials' ? 'Login to ZANORS' : 'Two-Factor Authentication'}
          </h3>
          <button onClick={closeModal}>
            <X size={24} />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-900 bg-opacity-50 border border-red-600 rounded text-red-200 text-sm">
            {error}
          </div>
        )}

        {loginStep === 'credentials' ? (
          <div className="space-y-4">
            <div className="flex space-x-2 bg-purple-900 p-1 rounded-lg">
              <button
                onClick={() => setLoginMethod('email')}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 ${
                  loginMethod === 'email' ? 'bg-blue-600' : 'bg-transparent'
                }`}
              >
                <Mail size={18} />
                <span>Email</span>
              </button>
              <button
                onClick={() => setLoginMethod('phone')}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 ${
                  loginMethod === 'phone' ? 'bg-blue-600' : 'bg-transparent'
                }`}
              >
                <Phone size={18} />
                <span>Phone</span>
              </button>
            </div>

            {loginMethod === 'email' ? (
              <div>
                <label className="block text-sm font-medium mb-2">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
                  placeholder="your@email.com"
                  onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
                  placeholder="+1234567890"
                  onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none pr-10"
                  placeholder="Enter your password"
                  onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-purple-300 hover:text-white"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="checkbox" className="rounded" />
                <span>Remember me</span>
              </label>
              <button className="text-blue-400 hover:text-blue-300">
                Forgot Password?
              </button>
            </div>

            <button
              onClick={handleLogin}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>

            <p className="text-sm text-center text-purple-300">
              Don't have an account?{' '}
              <button 
                onClick={() => setShowLoginModal(false)}
                className="text-blue-400 hover:text-blue-300 underline"
              >
                Sign up
              </button>
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-blue-900 bg-opacity-30 border border-blue-600 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-200">
                <strong>🔐 Two-Factor Authentication</strong>
              </p>
              <p className="text-sm text-blue-300 mt-1">
                For your security, we've sent a verification code to protect your account.
              </p>
            </div>

            <p className="text-center text-purple-200">
              Enter the code sent to<br />
              <span className="font-bold">{email || phone}</span>
            </p>

            <div>
              <label className="block text-sm font-medium mb-2 text-center">Verification Code</label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none text-center text-2xl tracking-widest"
                placeholder="000000"
                maxLength={6}
                autoFocus
                onKeyPress={(e) => e.key === 'Enter' && otp.length === 6 && handleVerifyOTP()}
              />
            </div>

            <button
              onClick={handleVerifyOTP}
              disabled={loading || otp.length !== 6}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Verifying...' : 'Verify & Login'}
            </button>

            <button
              onClick={() => setLoginStep('credentials')}
              className="w-full bg-gray-600 hover:bg-gray-700 text-white py-2 rounded-lg font-medium transition-colors"
            >
              Back
            </button>

            <p className="text-sm text-center text-purple-300">
              Didn't receive the code?{' '}
              <button 
                onClick={async () => {
                  try {
                    await supabase.auth.signInWithOtp({
                      email: email.toLowerCase(),
                      options: { shouldCreateUser: false }
                    });
                    alert('Verification code resent!');
                  } catch (error) {
                    setError('Failed to resend code.');
                  }
                }}
                className="text-blue-400 hover:text-blue-300 underline"
              >
                Resend
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginModal;
